'use client';

import Icon from '@/components/Icon';
import Price from '@/components/Price';
import StockStatusMessage from '@/components/StockStatusMessage';
import { useMiniCart } from '@/hooks/MiniCart/useMiniCart';
import { useToasts } from '@/hooks/Toasts/useToasts';

import { websiteEnv } from '@/constants/website-env';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { forwardRef, useEffect } from 'react';
import { AlertCircle as AlertCircleIcon } from 'react-feather';
import { toast } from 'sonner';
import { Drawer as DrawerPrimitive } from 'vaul';
import LoadingIndicator from '../LoadingIndicator';
import ProductList from './ProductList';
import operations from './miniCart.gql';
import RandomRelatedProducts from './random-related-products';

const errorIcon = (
  <Icon
    src={AlertCircleIcon}
    size={20}
  />
);

const MiniCart = props => {
  const { isOpen, setIsOpen, showNoItemDrawer, setShowNoItemDrawer, lastAddToCartTimestamp, isForAddToCartOk } = props;

  const talonProps = useMiniCart({
    isOpen,
    setIsOpen,
    operations,
  });
  const pathname = usePathname();
  const hideMiniCartDrawer = pathname === '/cart';

  const {
    errorMessage,
    handleRemoveItem,
    loading,
    productList,
    subTotal,
    configurableThumbnailSource,
    storeUrlSuffix,
    updatingUids,
    handleUpdateItemQuantity,
    miniCartLoading,
    hasOutOfStock,
  } = talonProps;

  const handleCloseMiniCart = () => {
    setIsOpen(false);
  };

  const isMobile = useIsMobile();
  const isCartEmpty = !(productList && productList.length);

  const [, { addToast }] = useToasts();

  useEffect(() => {
    if (errorMessage) {
      addToast({
        type: 'error',
        icon: errorIcon,
        message: errorMessage,
        dismissable: true,
        timeout: 7000,
      });
    }
  }, [addToast, errorMessage]);

  const contents = miniCartLoading ? (
    <div className='flex h-[320px] items-center justify-center'>
      <LoadingIndicator />
    </div>
  ) : isCartEmpty ? (
    <div className='flex h-[320px] items-center justify-center'>
      You have no item in your shopping {websiteEnv.isUnitedKingdom ? 'bag' : 'cart'}.
    </div>
  ) : (
    <>
      <div className='flex-none px-[18px] lg_px-6'>
        <div
          aria-live='polite'
          data-cy='CartPage-heading'
          className={clsx(
            'flex items-center gap-x-1.5 border-b-1  border-solid border-E7DED5',
            isForAddToCartOk ? 'justify-center pb-5' : 'justify-start py-2.5',
          )}
        >
          {isForAddToCartOk ? (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='41'
              height='40'
              fill='none'
              viewBox='0 0 41 40'
              className='h-auto w-[30px] lg_w-10'
            >
              <path
                stroke='#80583F'
                d='M1 19.996C1 9.226 9.73.5 20.501.5 31.27.5 40 9.226 40 19.996 40 30.768 31.269 39.5 20.501 39.5 9.731 39.5 1 30.768 1 19.996Z'
              />
              <path
                stroke='#80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.5'
                d='m29.166 12-11.2 13.333-4.8-5'
              />
            </svg>
          ) : null}
          <span
            className={
              isForAddToCartOk
                ? 'font-Shango text-[22px] leading-[26px] lg_text-[26px] lg_leading-8'
                : 'text-xl font-medium leading-[24px]'
            }
          >
            {isForAddToCartOk ? 'ITEM ADDED TO CART!' : 'Shopping Bag'}
          </span>
        </div>
      </div>
      <div className='flex-1 overflow-hidden'>
        <div className='theme-scroll-bar h-full overflow-auto pb-5'>
          <div className='px-[18px] lg_px-6'>
            <ProductList
              items={productList}
              loading={loading}
              handleRemoveItem={handleRemoveItem}
              closeMiniCart={handleCloseMiniCart}
              configurableThumbnailSource={configurableThumbnailSource}
              storeUrlSuffix={storeUrlSuffix}
              updatingUids={updatingUids}
              onUpdateItemQuantity={handleUpdateItemQuantity}
            />
            <RandomRelatedProducts
              lastAddToCartTimestamp={lastAddToCartTimestamp}
              onCloseMiniCart={handleCloseMiniCart}
            />
          </div>
        </div>
      </div>
      <div className='flex-none px-[18px] py-2.5 shadow-[0_0_4px_0_rgba(0,0,0,0.25)] lg_px-6'>
        {subTotal ? (
          <>
            <div className='flex items-center '>
              <span className=' text-base text-80583F '>ORDER TOTAL</span>
              <span className='ml-2.5 text-[13px] text-8B857D'>Tax included</span>
              <span className=' ml-auto text-[22px] font-medium leading-6 text-80583F'>
                <Price
                  currencyCode={subTotal.currency}
                  value={subTotal.value}
                />
              </span>
            </div>
            <div className='mt-2 text-sm'>
              <StockStatusMessage cartItems={productList} />
            </div>
          </>
        ) : null}

        <div className='mt-2.5 flex items-center gap-x-2.5'>
          <Link
            href='/cart'
            className={clsx(
              ' flex flex-1 items-center justify-center ',
              'h-[45px] rounded-[3px] text-[15px] leading-none lg_h-12',
              'border-1 border-solid border-80583F lg_hover_bg-80583F lg_hover_text-white',
            )}
            onClick={e => {
              if (hasOutOfStock) {
                e.preventDefault();

                toast.error('Out of stock items found in your cart');
              }

              handleCloseMiniCart();
            }}
          >
            {websiteEnv.isUnitedKingdom ? 'VIEW BAG' : 'VIEW CART'}
          </Link>
          <Link
            href='/checkout'
            className={clsx(
              ' flex flex-1 items-center justify-center ',
              'h-[45px] rounded-[3px] text-[15px] leading-none lg_h-12',
              'aw-primary-link',
            )}
            onClick={e => {
              if (hasOutOfStock) {
                e.preventDefault();

                toast.error('Out of stock items found in your cart');

                return;
              }

              handleCloseMiniCart();
            }}
          >
            CHECKOUT
          </Link>
        </div>
        <div className='mt-2.5 flex items-center justify-center gap-x-1.5 '>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='15'
            height='18'
            fill='none'
            viewBox='0 0 15 18'
          >
            <g clipPath='url(#clip0_13660_152344)'>
              <path
                fill='#99AD70'
                d='m10.611 14.687-2.345 2.01a1.26 1.26 0 0 1-1.662-.022l-2.492-2.248A10.17 10.17 0 0 1 .775 7.605L.503 3.81a1.24 1.24 0 0 1 .743-1.222L6.918.106c.323-.141.69-.141 1.013 0l5.823 2.55c.491.214.79.717.74 1.246l-.361 3.983a10.18 10.18 0 0 1-3.522 6.802'
              />
              <path
                fill='#fff'
                d='M6.5 11.5q-.02 0-.04-.003a.78.78 0 0 1-.572-.303L3.78 8.487a.857.857 0 0 1 .112-1.167.765.765 0 0 1 1.113.118l1.554 1.997 3.491-3.661a.765.765 0 0 1 1.117 0 .86.86 0 0 1 0 1.172l-4.108 4.31a.77.77 0 0 1-.56.243'
              />
            </g>
            <defs>
              <clipPath id='clip0_13660_152344'>
                <path
                  fill='#fff'
                  d='M.5 0h14v18H.5z'
                />
              </clipPath>
            </defs>
          </svg>

          <span className='text-[13px] leading-[18px] text-[#656363]'>Secure Checkout</span>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Drawer
        open={hideMiniCartDrawer ? false : isOpen}
        onOpenChange={setIsOpen}
        shouldScaleBackground={false}
        direction={isMobile ? 'bottom' : 'right'}
        modal
        handleOnly
      >
        <DrawerContent
          className={cn(
            'outline-0',
            'fixed bottom-0 right-0 lg_bottom-auto ',
            '  w-full shadow-md lg_max-w-[500px] ',
            'lg_top-0',
            'h-[calc(100vh_-_74px)] supports-[height:100dvh]_h-[calc(100dvh_-_74px)]  supports-[height:100dvh]_lg_h-[100dvh]',
            'bg-white',
            'z-[1100]',
            'flex flex-col pt-[30px] lg_pt-10',
          )}
        >
          <DrawerTitle className='hidden'>mini cart</DrawerTitle>
          <button
            className='absolute right-0 top-0 flex h-[46px] w-[46px] -translate-y-full items-center justify-center lg_translate-y-0'
            onClick={() => {
              setIsOpen(false);
            }}
            type='button'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              fill='none'
              viewBox='0 0 18 18'
            >
              <path
                className='stroke-white lg_stroke-80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.5'
                d='M17 17 1 1m16 0L1 17'
              />
            </svg>
          </button>
          {contents}
        </DrawerContent>
      </Drawer>
      {isMobile ? (
        <Drawer
          direction='bottom'
          shouldScaleBackground={false}
          open={hideMiniCartDrawer ? false : showNoItemDrawer}
          onOpenChange={() => {
            setShowNoItemDrawer(false);
          }}
        >
          <DrawerContent className='bottom-0 left-0 z-[1100] h-[88%] w-full '>
            <DrawerTitle className='hidden'>
              You have no item in your shopping {websiteEnv.isUnitedKingdom ? 'bag' : 'cart'}.
            </DrawerTitle>
            <div className='h-full bg-white'>
              <div className='flex justify-end'>
                <button
                  type='button'
                  className='flex h-[70px] w-[50px] items-center justify-center'
                  onClick={() => {
                    setShowNoItemDrawer(false);
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    fill='none'
                    viewBox='0 0 16 16'
                  >
                    <path
                      stroke='#80583F'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='M15 15 1 1m14 0L1 15'
                    />
                  </svg>
                </button>
              </div>
              <div className='mt-3 text-center text-sm leading-5'>
                You have no item in your shopping {websiteEnv.isUnitedKingdom ? 'bag' : 'cart'}.
              </div>

              <button
                type='button'
                className={clsx(
                  ' flex items-center justify-center ',
                  'h-12 w-full max-w-[250px]',
                  'rounded-[3px]',
                  'bg-80583F text-sm font-medium text-white',
                  'mx-auto mt-[42px]',
                )}
                onClick={() => {
                  setShowNoItemDrawer(false);
                }}
              >
                CONTINUE SHOPPING
              </button>
            </div>
          </DrawerContent>
        </Drawer>
      ) : null}
    </>
  );
};

export default MiniCart;

const Drawer = ({ shouldScaleBackground = true, ...props }) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
);

Drawer.displayName = 'Drawer';

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerOverlay = forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-[1000] bg-black/60', className)}
    {...props}
  />
));

DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = forwardRef(({ className, children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn('fixed', className)}
      aria-describedby={undefined}
      {...props}
    >
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));

DrawerContent.displayName = 'DrawerContent';

const DrawerTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));

DrawerTitle.displayName = DrawerPrimitive.Title.displayName;
